/**
 * 청구 일정 목록 테이블 컬럼 정의
 * 청구 일정 목록을 표시하기 위한 테이블 컬럼 설정을 정의합니다.
 *
 * 포함된 컬럼:
 * - 빌링 스케줄 ID: scheduleId 표기. 링크는 빌링 스케줄 상세(contractId 기준)
 * - 계약 ID: 신청/계약 상세 링크 (신청번호 대체)
 * - 상태: 계약 상태
 * - 사업자: 계약 사업자 정보
 * - 계약자명: 계약자 이름
 * - 계약명: 계약 이름
 * - 상품유형: 공간 상품 유형
 * - 관리비: 관리비 포함 여부
 * - 상품명: 공간 상품 이름
 * - 청구 종류: 직전 청구서의 생성출처(generationSource)
 * - 최근 청구금액: 최근 청구된 금액
 * - 결제상태: 현재 결제 상태
 * - 최근 납부기한: 최근 납부 기한
 * - 연체여부: 연체 상태
 * - 빌링 기간: 청구 기간
 */

import _ from "lodash";
import moment from "moment";
import { generationSourceLabel, generationSourceList } from "src/api/billingSchedule/billing-type";
import { BaseTooltip } from "src/components";
import { BaseContractStatus } from "src/components/BaseContractStatus";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import DetailLink from "src/components/DetailLink";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import PagePath from "src/pagePath.json";
import { numberToStringWithComma, ViewYmdFormat } from "src/utils/common-util";
import PaymentStatusView from "../../components/PaymentStatusView";

/**
 * 청구 일정 목록 테이블 컬럼 정의
 * 각 컬럼의 헤더, 접근자, 너비, 셀 렌더링 등을 설정합니다.
 */
const BillIngScheduleColumns: any = [
  {
    // 표기는 이용료(RENTAL) 스케줄의 scheduleId, 이동은 빌링 스케줄 상세(contractId 기준)다.
    // 빌링 스케줄 상세가 계약 단위 화면(GET /ct/contract/{contractId}/schedule)이라 링크 키는 contractId 를 유지한다.
    // 표기값과 정렬 키가 어긋나므로 정렬은 비활성(BillingScheduleList 의 disabledSortHeaders)한다.
    Header: "빌링 스케줄 ID",
    accessor: "scheduleId",
    width: 120,
    sticky: "left",
    Cell: ({ value, row, isS2Contract }: any) => {
      const billingPath = PagePath.billing.detail.replace(":id", row.original.contractId);
      const billingS2Path = PagePath.billingS2.detail.replace(":id", row.original.contractId);

      return (
        <>
          <DetailLink to={isS2Contract ? billingS2Path : billingPath} className={"text-underline"}>
            {value || "-"}
          </DetailLink>
        </>
      );
    },
  },
  {
    // 신청번호(난수) 대체 컬럼. 빌링 스케줄 ID 컬럼은 빌링 스케줄 상세로, 이 컬럼은 신청/계약 상세로 이동한다.
    Header: "계약 ID",
    accessor: "contractId",
    width: 120,
    sticky: "left",
    Cell: (props: any) => {
      const isS2Contract = props.isS2Contract;
      const contractDetailPath = PagePath.contract.detail.replace(":id", props.row.original.contractId);
      const contractS2DetailPath = PagePath.contractS2.detail.replace(":id", props.row.original.contractId);

      return (
        <>
          <BaseNewTabLink path={isS2Contract ? contractS2DetailPath : contractDetailPath} value={String(props.value ?? "-")} ellipsis />
        </>
      );
    },
  },
  {
    Header: "상태",
    accessor: "contractStep",
    width: 80,
    sticky: "left",
    Cell: ({ value, row }: any) => {
      return <BaseContractStatus status={value} />;
    },
  },
  {
    Header: "계약자",
    accessor: "mbOrganizationName",
    sticky: "left",
    width: 160,
    Cell: ({ value, row }: any) => {
      const path = PagePath.organization.detail.replace(":id", row.original.mbOrganizationId);

      // 해당없음인 경우
      if (!row.original.mbOrganizationId || row.original.mbOrganizationId === "0" || row.original.mbOrganizationId === 0) {
        return <div className="w-100 text-left">-</div>;
      }

      // 개인/사업자 구분
      const classifications = row.original.mbOrganizationClassifications || [];
      const isIndividual = classifications.includes("INDIVIDUAL");

      // 개인인 경우 ceoName(이름) 표시
      if (isIndividual) {
        const displayName = row.original.mbOrganizationCeoName || "-";
        return (
          <div className="w-100">
            {displayName !== "-" ? (
              <BaseNewTabLink className="text-left" path={path} value={displayName} tooltip={true} />
            ) : (
              <div className="text-left">-</div>
            )}
          </div>
        );
      }

      // 사업자인 경우 name(법인명/상호) 표시
      return <div className="w-100">{value ? <BaseNewTabLink className="text-left" path={path} value={value || "-"} tooltip={true} /> : "-"}</div>;
    },
  },
  {
    Header: "계약자명",
    accessor: "contractorName",
    width: 120,
    sticky: "left",
    Cell: ({ value, row }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={value} /> : "-"}</div>;
    },
  },
  {
    Header: "계약명",
    accessor: "contractName",
    width: 120,
    sticky: "left",
    Cell: ({ value, row }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={value} /> : "-"}</div>;
    },
  },
  {
    Header: "상품유형",
    accessor: "spaceProductType",
    width: 80,
    Cell: (props: any) => {
      return <ProductTypeChangeLabel productType={props.value} />;
    },
  },
  {
    Header: "관리비",
    accessor: "isMntContain",
    width: 80,
    Cell: (props: any) => {
      return <p>{props.value ? "-" : "별도"}</p>;
    },
  },
  {
    Header: "상품명",
    accessor: "spaceProductName",
    width: 160,
    Cell: ({ value, row }: any) => {
      const path = PagePath.product.detail.replace(":id", row.original.spaceProductId);
      return (
        <div className="w-100 text-left">
          <BaseNewTabLink path={path} value={value || "-"} ellipsis />
        </div>
      );
    },
  },
  {
    // 헤더의 ? 아이콘에 청구 종류별 설명 툴팁을 노출한다. (방문자 목록의 주차 차량 번호 컬럼과 동일한 패턴)
    Header: () => (
      <div className="d-flex flex-center">
        <span>청구 종류</span>
        <BaseTooltip touchIcon={"QUESTION"} size={16} className="ml4">
          <ul className="base-list">
            {generationSourceList.map(({ value, label, tooltip }) => (
              <li key={value} className="font13 text-primary">
                {label} : {tooltip}
              </li>
            ))}
          </ul>
        </BaseTooltip>
      </div>
    ),
    // 직전 청구서의 generationSource 를 라벨로 치환한다. 선택 인터랙션은 없다.
    // 라벨이 없는 값(OVERDUE)과 직전 청구서가 없는 행은 "-" 로 표기한다.
    accessor: "generationSource",
    width: 140,
    Cell: ({ value }: any) => {
      return <div className="w-100 text-left">{generationSourceLabel[String(value)] || "-"}</div>;
    },
  },
  {
    Header: "최근 청구금액(원)",
    width: 160,
    accessor: "billAmount",
    Cell: ({ value, row }: any) => {
      return <p className="w-100">{Number(row.original.billAmount) > 0 ? numberToStringWithComma(value) : "-"}</p>;
    },
  },
  {
    Header: "결제상태",
    width: 80,
    accessor: "billPayStatus",
    Cell: ({ value, row }: any) => {
      return <PaymentStatusView value={value} contractStep={row.original.contractStep} />;
    },
  },
  {
    Header: "최근 납부기한",
    width: 200,
    accessor: "billDueDate",
    Cell: ({ value, row }: any) => {
      return <p>{row.original.billDueDate ? moment(row.original.billDueDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS) : "-"}</p>;
    },
  },
  {
    Header: "연체여부",
    accessor: "isOverdue",
    width: 80,
    Cell: ({ value, row }: any) => {
      // console.log("contractStep", row.original.contractStep !== "APPLY_CANCEL");
      return (
        <div
          className={`${
            Number(row.original.billAmount) > 0 &&
            ((value === undefined && "status F") || (String(value) === "false" && "status F") || (String(value) === "true" && "status G"))
          }`}
        >
          {Number(row.original.billAmount) > 0 ? (String(value) === "true" ? "연체" : "정상") : "-"}
        </div>
      );
    },
  },
  {
    Header: "빌링 기간",
    accessor: "scheduleStartTime",
    width: 200,
    Cell: ({ value, row }: any) => {
      const start = moment(row.original.scheduleStartTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK);
      const end = moment(row.original.scheduleEndTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK);
      if (_.isEqual(start, end)) {
        return <p className="w-100 text-left">{row.original.scheduleStartTime ? start : "-"}</p>;
      } else {
        return <p className="w-100 text-left">{row.original.scheduleStartTime ? start + " ~ " + end : "-"}</p>;
      }
    },
  },
];

export default BillIngScheduleColumns;
